"use client";

import { AlertCircle, AlertTriangle } from "lucide-react";

import { Badge } from "@/lib/components/badge";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@/lib/components/tooltip";

import { useI18n } from "@llmgateway/i18n/react";

interface ModelStatusBadgeProps {
	status: "deactivated" | "deprecated";
	isPast: boolean;
}

export function ModelStatusBadge({ status, isPast }: ModelStatusBadgeProps) {
	const { t } = useI18n();
	const isDeactivated = status === "deactivated";

	const title = isPast
		? isDeactivated
			? t("public.models.status.deactivated")
			: t("public.models.status.deprecated")
		: isDeactivated
			? t("public.models.status.scheduledDeactivation")
			: t("public.models.status.scheduledDeprecation");

	const description = isPast
		? isDeactivated
			? t("public.models.status.deactivatedDescription")
			: t("public.models.status.deprecatedDescription")
		: isDeactivated
			? t("public.models.status.scheduledDeactivationDescription")
			: t("public.models.status.scheduledDeprecationDescription");

	return (
		<TooltipProvider delayDuration={300}>
			<Tooltip>
				<TooltipTrigger asChild>
					<Badge
						variant="outline"
						className={`text-xs md:text-sm px-2 md:px-3 py-1 gap-1.5 cursor-help ${
							isDeactivated
								? "bg-red-50 dark:bg-red-500/5 text-red-700 dark:text-red-400 border-red-200 dark:border-red-500/20"
								: "bg-amber-50 dark:bg-amber-500/5 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-500/20"
						}`}
					>
						{isDeactivated ? (
							<AlertCircle className="h-3 w-3" />
						) : (
							<AlertTriangle className="h-3 w-3" />
						)}
						{title}
					</Badge>
				</TooltipTrigger>
				<TooltipContent side="bottom" className="max-w-xs">
					<p className="text-xs">{description}</p>
				</TooltipContent>
			</Tooltip>
		</TooltipProvider>
	);
}
